import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import useSEO from "../hooks/useSEO";

// Import images from assets/team
import team1 from '../assets/team/odontoiatria_sgarbini-66.webp'
import team2 from '../assets/team/odontoiatria_sgarbini-68.webp'
import team3 from '../assets/team/odontoiatria_sgarbini-75.webp'
import team4 from '../assets/team/odontoiatria_sgarbini-115.webp'
import team5 from '../assets/team/odontoiatria_sgarbini-120.webp'
import team6 from '../assets/team/odontoiatria_sgarbini-125.webp'
import team7 from '../assets/team/odontoiatria_sgarbini-181.webp'
import team8 from '../assets/team/odontoiatria_sgarbini-213.webp'

// Single team member card component
const TeamCard = ({
  name,
  role,
  department,
  img,
  show,
}: {
  name: string
  role: string
  department: string
  img: string
  show: boolean
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`
        bg-white rounded-2xl shadow-xl overflow-hidden w-full max-w-xs flex flex-col
        transition-all duration-700 ease-out
        ${show ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}
      `}
      style={{
        willChange: 'opacity, transform',
      }}
    >
      <div className="aspect-[3/4] w-full bg-gray-100 relative">
        {/* Placeholder mentre l'immagine si carica */}
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse flex items-center justify-center">
            <div className="w-16 h-16 bg-gray-300 rounded-full animate-pulse"></div>
          </div>
        )}
        
        <img
          ref={imgRef}
          src={isVisible ? img : undefined}
          alt={name}
          className={`w-full h-full object-cover object-top transition-opacity duration-500 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageLoaded(true)} // Mostra il placeholder anche in caso di errore
        />
      </div>
      <div className="flex flex-col items-center px-4 py-5 bg-white border-t border-gray-100">
        <div className="font-bold text-2xl text-gray-900 text-center mb-1">{name}</div>
        <div className="text-xl text-sky-700 text-center italic mb-1">{role}</div>
        <div className="text-lg text-gray-500 text-center">{department}</div>
      </div>
    </div>
  );
};



const ChiSiamo = () => {
  const { t } = useTranslation();
  
  const team = [
    { 
      name: t('chiSiamo.team.member1.name'), 
      role: t('chiSiamo.team.member1.role'), 
      department: t('chiSiamo.team.member1.department'), 
      img: team1 
    },
    { 
      name: t('chiSiamo.team.member2.name'), 
      role: t('chiSiamo.team.member2.role'), 
      department: t('chiSiamo.team.member2.department'), 
      img: team3 
    },
    { 
      name: t('chiSiamo.team.member3.name'), 
      role: t('chiSiamo.team.member3.role'), 
      department: t('chiSiamo.team.member3.department'), 
      img: team2 
    },
    { 
      name: t('chiSiamo.team.member4.name'), 
      role: t('chiSiamo.team.member4.role'), 
      department: t('chiSiamo.team.member4.department'), 
      img: team4 
    },
    { 
      name: t('chiSiamo.team.member5.name'), 
      role: t('chiSiamo.team.member5.role'), 
      department: t('chiSiamo.team.member5.department'), 
      img: team5 
    },
    { 
      name: t('chiSiamo.team.member6.name'), 
      role: t('chiSiamo.team.member6.role'), 
      department: t('chiSiamo.team.member6.department'), 
      img: team6 
    },
    { 
      name: t('chiSiamo.team.member7.name'), 
      role: t('chiSiamo.team.member7.role'), 
      department: t('chiSiamo.team.member7.department'), 
      img: team7 
    },
    { 
      name: t('chiSiamo.team.member8.name'), 
      role: t('chiSiamo.team.member8.role'), 
      department: t('chiSiamo.team.member8.department'), 
      img: team8 
    },
  ];
  const [showCards, setShowCards] = useState(Array(team.length).fill(false))

  // SEO Configuration for Chi Siamo Page
  useSEO({
    title: t('chiSiamo.seo.title'),
    description: t('chiSiamo.seo.description'),
    keywords: t('chiSiamo.seo.keywords'),
    schema: {
      "@context": "https://schema.org",
      "@type": "MedicalOrganization",
      "name": "Studio Dentistico Sgarbini",
      "description": t('chiSiamo.seo.schemaDescription'),
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Via Camilla 10/a",
        "addressLocality": "Roma",
        "addressCountry": "IT"
      },
      "telephone": "+39 06 7886902",
      "medicalSpecialty": [
        t('chiSiamo.seo.specialties.general'),
        t('chiSiamo.seo.specialties.hygiene'),
        t('chiSiamo.seo.specialties.orthodontics'),
        t('chiSiamo.seo.specialties.surgery'),
        t('chiSiamo.seo.specialties.pediatric')
      ],
      "employee": team.map(member => ({
        "@type": "Person",
        "name": member.name,
        "jobTitle": member.role,
        "worksFor": {
          "@type": "MedicalOrganization",
          "name": "Studio Dentistico Sgarbini"
        }
      }))
    }
  });

  useEffect(() => {
    // Staggered animation
    team.forEach((_, i) => {
      setTimeout(() => {
        setShowCards(prev =>
          prev.map((val, idx) => (idx === i ? true : val))
        )
      }, 150 * i)
    })
  }, [])

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <h1 className="text-5xl font-bold mb-12 text-center">{t('chiSiamo.title')}</h1>
      <div className="flex flex-wrap justify-center gap-10">
        {team.map((member, i) => (
          <TeamCard
            key={member.name}
            name={member.name}
            role={member.role}
            department={member.department}
            img={member.img}
            show={showCards[i]}
          />
        ))}
      </div>
    </div>
  )
}

export default ChiSiamo